import React, { useState, useEffect } from "react";
import { Button, Modal, Typography, notification as antNotification } from "antd";
import { BellOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useTheme } from "utils/global/ThemeContext";
import * as notificationsAPI from "utils/portal-apis/notifications";

const { Title, Text } = Typography;

const StyledModal = styled(Modal)`
  top: 20px !important; /* Reduced top margin to move modal up */
  
  .ant-modal-content {
    background-color: ${props => props.theme?.colors?.background?.modal || '#1a1a1a'};
  }
  
  .ant-modal-header {
    background-color: ${props => props.theme?.colors?.background?.modal || '#1a1a1a'};
    border-bottom: 1px solid ${props => props.theme?.colors?.border?.base || '#303030'};
  }
  
  .ant-modal-title {
    color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  }
  
  .ant-modal-close {
    color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  }
  
  .ant-modal-body {
    background-color: ${props => props.theme?.colors?.background?.modal || '#1a1a1a'};
    padding: 0; /* Remove default padding to control layout precisely */
    height: 600px; /* Fixed height for proper scrolling */
    display: flex;
    flex-direction: column;
  }
`;

const NotificationButton = styled(Button)`
  background: transparent;
  border: none;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  
  &:hover {
    background-color: ${props => props.theme?.colors?.surface?.level3 || '#2b2b2b'};
    color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  }
  
  &:focus {
    background-color: ${props => props.theme?.colors?.surface?.level3 || '#2b2b2b'};
    color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  }
`;

const NotificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const NotificationContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  overflow-y: auto; /* Make content scrollable */
  padding-bottom: 80px; /* Add padding to prevent content from being hidden behind footer */
  
  /* Custom scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme?.colors?.border?.base || '#303030'} transparent;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme?.colors?.border?.base || '#303030'};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${props => props.theme?.colors?.primary || '#1890ff'};
  }
`;

const FeaturedMedia = styled.div`
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  max-width: 100%;
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: cover;
  display: block;
`;

const FeaturedVideo = styled.video`
  width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: cover;
  display: block;
`;

const MessageContent = styled.div`
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  flex: 1;
  font-size: 16px;
  
  p {
    margin: 0 0 12px 0;
    line-height: 1.6;
    font-size: 16px;
  }
  
  a {
    color: ${props => props.theme?.colors?.primary || '#1890ff'};
    font-size: 16px;
  }
  
  strong, b {
    color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
    font-weight: 900 !important;
    font-size: 18px;
  }
  
  em, i {
    font-style: italic;
    font-size: 16px;
  }
  
  u {
    text-decoration: underline;
    font-size: 16px;
  }
  
  ol, ul {
    margin: 0 0 12px 0;
    padding-left: 20px;
    font-size: 16px;
  }
  
  li {
    margin-bottom: 4px;
    font-size: 16px;
  }
`;

const NavigationFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: ${props => props.theme?.colors?.background?.modal || '#1a1a1a'};
  border-top: 1px solid ${props => props.theme?.colors?.border?.base || '#303030'};
  z-index: 10;
`;

const PaginationDots = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  flex: 1;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const ButtonContainer = styled.div`
  width: 100px; /* Fixed width to maintain spacing */
  display: flex;
  justify-content: ${props => props.align === 'left' ? 'flex-start' : 'flex-end'};
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => 
    props.$active 
      ? props.theme?.colors?.primary || '#1890ff'
      : props.theme?.colors?.border?.base || '#303030'
  };
  transition: background-color 0.2s ease;
`;

const NavButton = styled(Button)`
  background: ${props => props.theme?.colors?.surface?.level3 || '#2b2b2b'};
  border: 1px solid ${props => props.theme?.colors?.border?.base || '#303030'};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  
  &:hover {
    background-color: ${props => props.theme?.colors?.surface?.level4 || '#3a3a3a'};
    border-color: ${props => props.theme?.colors?.primary || '#1890ff'};
    color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  }
  
  &:disabled {
    background-color: ${props => props.theme?.colors?.surface?.level2 || '#1f1f1f'};
    border-color: ${props => props.theme?.colors?.border?.base || '#303030'};
    color: ${props => props.theme?.colors?.text?.secondary || '#8c8c8c'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.theme?.colors?.text?.secondary || '#8c8c8c'};
`;

const NotificationPopover = ({ userId }) => {
  const { theme } = useTheme();
  const [visible, setVisible] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasCheckedAutoOpen, setHasCheckedAutoOpen] = useState(false);

  // Helper function to determine if media is video or image
  const getMediaType = (mediaUrl) => {
    if (!mediaUrl) return null;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const extension = mediaUrl.toLowerCase().split('.').pop();
    return videoExtensions.includes(`.${extension}`) ? 'video' : 'image';
  };

  const fetchNotifications = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const response = await notificationsAPI.getUserNotifications(userId);
      
      if (response && Array.isArray(response)) {
        setNotifications(response);
        return response;
      } else if (response?.data && Array.isArray(response.data)) {
        setNotifications(response.data);
        return response.data;
      } else {
        setNotifications([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      antNotification.error({
        message: "Error",
        description: "Failed to load notifications"
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId, userId);
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif._id === notificationId 
            ? { ...notif, read: true }
            : notif
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleNotificationView = async () => {
    const currentNotification = notifications[currentIndex];
    if (currentNotification && !currentNotification.read) {
      await markAsRead(currentNotification._id);
    }
  };

  const handlePrevious = () => {
    // Previous goes to older notifications (higher index)
    const newIndex = Math.min(notifications.length - 1, currentIndex + 1);
    setCurrentIndex(newIndex);
  };

  const handleNext = () => {
    // Next goes to newer notifications (lower index)
    const newIndex = Math.max(0, currentIndex - 1);
    setCurrentIndex(newIndex);
  };

  const handleModalOpen = () => {
    setVisible(true);
    setCurrentIndex(0);
    fetchNotifications();
  };

  const handleModalClose = () => {
    setVisible(false);
  };

  // Auto-open modal if most recent notification is unread
  useEffect(() => {
    if (userId && !hasCheckedAutoOpen) {
      const checkForUnreadRecent = async () => {
        const notificationsList = await fetchNotifications();
        
        // Check if the most recent notification (index 0) is unread
        if (notificationsList.length > 0 && !notificationsList[0].read) {
          setVisible(true);
          setCurrentIndex(0);
        }
        
        setHasCheckedAutoOpen(true);
      };
      
      checkForUnreadRecent();
    }
  }, [userId, hasCheckedAutoOpen]);

  // Mark current notification as read when viewed
  React.useEffect(() => {
    if (visible && notifications.length > 0) {
      handleNotificationView();
    }
  }, [currentIndex, visible, notifications]);

  return (
    <>
      <NotificationButton 
        theme={theme}
        icon={<BellOutlined style={{ fontSize: '18px' }} />}
        onClick={handleModalOpen}
      />

      <StyledModal
        title={notifications.length > 0 ? notifications[currentIndex]?.title : "Notifications"}
        open={visible}
        onCancel={handleModalClose}
        footer={null}
        width={700}
        theme={theme}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Text style={{ color: theme?.colors?.text?.secondary }}>Loading notifications...</Text>
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState theme={theme}>
            <BellOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
            <div>No notifications yet</div>
          </EmptyState>
        ) : (
          <NotificationContainer>
            <NotificationContent theme={theme}>
              {/* Featured Media - Image or Video */}
              {(() => {
                const currentNotification = notifications[currentIndex];
                const hasFeaturedMedia = !!currentNotification?.featured_media;
                
                if (!hasFeaturedMedia) {
                  return null;
                }
                
                return (
                  (() => {
                    const mediaUrl = currentNotification.featured_media;
                    const mediaType = getMediaType(mediaUrl);
                    
                    if (!mediaUrl) {
                      return null;
                    }
                    
                    return mediaType === 'video' ? (
                      <FeaturedVideo 
                        key={currentNotification._id}
                        src={mediaUrl}
                        controls
                        preload="metadata"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          objectPosition: 'center',
                          display: 'block',
                          marginBottom: '20px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'block';
                          e.target.style.backgroundColor = '#f5f5f5';
                          e.target.style.border = '2px dashed #d9d9d9';
                          e.target.style.padding = '20px';
                          e.target.innerHTML = '⚠️ Failed to load video';
                        }}
                      />
                    ) : (
                      <FeaturedImage 
                        key={currentNotification._id}
                        src={mediaUrl}
                        alt="Featured notification media"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          objectPosition: 'center',
                          display: 'block',
                          marginBottom: '20px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'block';
                          e.target.style.backgroundColor = '#f5f5f5';
                          e.target.style.border = '2px dashed #d9d9d9';
                          e.target.style.padding = '20px';
                          e.target.style.minHeight = '100px';
                          e.target.style.textAlign = 'center';
                          e.target.style.color = '#666';
                          e.target.alt = '⚠️ Failed to load image';
                        }}
                      />
                    );
                  })()
                );
              })()}
              
              <MessageContent 
                theme={theme}
                dangerouslySetInnerHTML={{ __html: notifications[currentIndex]?.message }}
              />
            </NotificationContent>
            
            <NavigationFooter theme={theme}>
              <ButtonContainer align="left">
                {currentIndex < notifications.length - 1 ? (
                  <NavButton
                    theme={theme}
                    icon={<LeftOutlined />}
                    onClick={handlePrevious}
                  >
                    Previous
                  </NavButton>
                ) : null}
              </ButtonContainer>
              
              <PaginationDots>
                {notifications.map((_, index) => (
                  <Dot
                    key={index}
                    theme={theme}
                    $active={index === (notifications.length - 1 - currentIndex)}
                  />
                ))}
              </PaginationDots>
              
              <ButtonContainer align="right">
                {currentIndex > 0 ? (
                  <NavButton
                    theme={theme}
                    icon={<RightOutlined />}
                    onClick={handleNext}
                  >
                    Next
                  </NavButton>
                ) : null}
              </ButtonContainer>
            </NavigationFooter>
          </NotificationContainer>
        )}
      </StyledModal>
    </>
  );
};

export default NotificationPopover; 